import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { useUrlFilterSync } from '../hooks';

export const messages = defineMessages({
  label: {
    id: 'appUpdates.filters.apply.label',
    defaultMessage: 'Apply',
    description: 'Label for the apply filters button',
  },
});

interface ApplyButtonProps {}

/**
 * Apply button component for app updates filters.
 * Commits the current draft filter state to the URL and context, triggering a data refetch in the parent component.
 */
export const ApplyButton: React.FC<ApplyButtonProps> = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useUrlFilterSync();
  const { sendUIEvent } = useAnalyticsEvent();

  const handleClick = React.useCallback(() => {
    syncFiltersToUrl({
      ...state.updates.draftFilters,
      cursor: undefined,
    });

    dispatch({
      type: 'UPDATES_FILTERS_APPLY',
    });

    // Send analytics event with current filter state
    const updatesAppTypes =
      state.updates.draftFilters.selectedProducts &&
      state.updates.draftFilters.selectedProducts.length > 0
        ? state.updates.draftFilters.selectedProducts.map((p) => p.id).join(',')
        : undefined;
    const updatesStatusTypes =
      state.updates.draftFilters.selectedStatuses &&
      state.updates.draftFilters.selectedStatuses.length > 0
        ? state.updates.draftFilters.selectedStatuses.map((s) => s.id).join(',')
        : undefined;
    const updatesChangeTypes =
      state.updates.draftFilters.selectedChangeTypes &&
      state.updates.draftFilters.selectedChangeTypes.length > 0
        ? state.updates.draftFilters.selectedChangeTypes
            .map((c) => c.id)
            .join(',')
        : undefined;
    const updatesSearchContent =
      state.updates.draftFilters.searchQuery || undefined;

    sendUIEvent(
      appUpdatesUIEvents.ui.appUpdatesApplyClicked(
        { source: 'appUpdatesListScreen' },
        updatesAppTypes,
        updatesStatusTypes,
        updatesChangeTypes,
        updatesSearchContent,
      ).data,
    );
  }, [dispatch, state.updates.draftFilters, syncFiltersToUrl, sendUIEvent]);

  return (
    <Button
      testId="app-updates-apply-filter-button"
      onClick={handleClick}
      appearance="primary"
    >
      {formatMessage(messages.label)}
    </Button>
  );
};
